import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  Apple,
  Camera,
  Mic,
  MoreHorizontal,
  Settings,
  X,
} from "lucide-react";

const T = {
  primary50:      "#EFF6FF",
  primary100:     "#DBEAFE",
  primary400:     "#3B82F6",
  primary500:     "#2563EB",
  warning500:     "#F59E0B",
  warning700:     "#B45309",
  neutral100:     "#F9FAFB",
  neutral200:     "#E5E7EB",
  neutral400:     "#9CA3AF",
  neutral600:     "#4B5563",
  neutral700:     "#374151",
  neutral900:     "#111827",
  white:          "#FFFFFF",
  overlay:        "rgba(0,0,0,0.5)",
  shadowModal:    "0 8px 24px rgba(0,0,0,0.16)",
  fast:           "150ms ease-in-out",
  zOverlay:       300,
  zModal:         400,
};

const font = "'Segoe UI', system-ui, -apple-system, sans-serif";

type BrowserKey = "chrome" | "safari" | "firefox" | "edge" | "ios" | "android";

const STEP_ICONS: Record<string, React.ReactNode> = {
  tune: (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: 22, height: 22, borderRadius: 5,
      backgroundColor: T.neutral100, border: `1px solid ${T.neutral200}`,
      margin: "0 3px", verticalAlign: "middle", flexShrink: 0,
    }}>
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none"
           stroke={T.neutral600} strokeWidth="1.5" strokeLinecap="round">
        <line x1="2" y1="5" x2="14" y2="5"/>
        <circle cx="6"  cy="5"  r="1.8" fill={T.neutral100} stroke={T.neutral600} strokeWidth="1.5"/>
        <line x1="2" y1="11" x2="14" y2="11"/>
        <circle cx="10" cy="11" r="1.8" fill={T.neutral100} stroke={T.neutral600} strokeWidth="1.5"/>
      </svg>
    </span>
  ),
  lock: (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: 22, height: 22, borderRadius: 5,
      backgroundColor: T.neutral100, border: `1px solid ${T.neutral200}`,
      margin: "0 3px", verticalAlign: "middle", flexShrink: 0,
    }}>
      <svg width="12" height="12" viewBox="0 0 16 16" fill="none"
           stroke={T.neutral600} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="7" width="10" height="8" rx="2"/>
        <path d="M5 7V5a3 3 0 0 1 6 0v2"/>
        <circle cx="8" cy="11" r="1" fill={T.neutral600} stroke="none"/>
      </svg>
    </span>
  ),
  gear: (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: 22, height: 22, borderRadius: 5,
      backgroundColor: T.neutral100, border: `1px solid ${T.neutral200}`,
      margin: "0 3px", verticalAlign: "middle", flexShrink: 0,
    }}>
      <Settings size={13} color={T.neutral600} strokeWidth={1.8} />
    </span>
  ),
  safarimenu: (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: "1px 7px", borderRadius: 4,
      backgroundColor: T.neutral100, border: `1px solid ${T.neutral200}`,
      margin: "0 3px", verticalAlign: "middle", flexShrink: 0,
      fontSize: 11, fontWeight: 500, color: T.neutral700, fontFamily: font,
      whiteSpace: "nowrap",
    }}>
      Safari
    </span>
  ),
};

// Resolves {{perm}} / {{Perm}} tokens before icon-token parsing
function resolveText(text: string, lower: string, title: string): string {
  return text.replace(/{{perm}}/g, lower).replace(/{{Perm}}/g, title);
}

function renderStep(text: string): React.ReactNode {
  const parts = text.split(/({{[a-z]+}})/);
  return parts.map((part, i) => {
    const match = part.match(/^{{([a-z]+)}}$/);
    if (match && STEP_ICONS[match[1]]) {
      return <span key={i} style={{ display: "inline" }}>{STEP_ICONS[match[1]]}</span>;
    }
    return part;
  });
}

interface BrowserInstruction {
  title: string;
  subtitle: string;
  steps: string[];
  ref: { label: string; url: string };
}

// {{perm}} = lowercase permission label, {{Perm}} = title case
const INSTRUCTIONS: Record<BrowserKey, BrowserInstruction> = {
  chrome: {
    title:    "Allow {{perm}} access in Chrome",
    subtitle: "Follow these steps, then refresh the page.",
    steps: [
      "Click the {{tune}} icon at the far left of the address bar.",
      "Toggle {{Perm}} on in the popup.",
      "Refresh this page to apply the change.",
    ],
    ref: { label: "Google Support", url: "https://support.google.com/chrome/answer/2693767?co=GENIE.Platform%3DDesktop" },
  },
  safari: {
    title:    "Allow {{perm}} access in Safari",
    subtitle: "Follow these steps, then refresh the page.",
    steps: [
      "Click {{safarimenu}} in the menu bar, then select Settings.",
      "Click the Websites tab, select {{Perm}} in the left sidebar. Find this site and set it to Allow.",
      "Refresh this page and permissions will take effect.",
    ],
    ref: { label: "Apple Support", url: "https://support.apple.com/guide/safari/websites-ibrwe2159f50/mac" },
  },
  firefox: {
    title:    "Allow {{perm}} access in Firefox",
    subtitle: "Follow these steps, then refresh the page.",
    steps: [
      "Click the {{lock}} icon at the far left of the address bar.",
      "In the panel, set {{Perm}} to Allow.",
      "Refresh this page to apply the change.",
    ],
    ref: { label: "Mozilla Support", url: "https://support.mozilla.org/en-US/kb/how-manage-your-camera-and-microphone-permissions" },
  },
  edge: {
    title:    "Allow {{perm}} access in Microsoft Edge",
    subtitle: "Follow these steps, then refresh the page.",
    steps: [
      "Click the {{tune}} icon at the far left of the address bar.",
      "Toggle {{Perm}} on in the popup.",
      "Refresh this page to apply the change.",
    ],
    ref: { label: "Microsoft Support", url: "https://support.microsoft.com/en-us/windows/windows-camera-microphone-and-privacy-a83257bc-e990-d54a-d212-b5e41beba857" },
  },
  ios: {
    title:    "Allow {{perm}} access on iOS",
    subtitle: "Follow these steps, then refresh the page.",
    steps: [
      "Open the {{gear}} Settings app on your iPhone.",
      "Go to Apps › Safari › Settings for Websites, then set {{Perm}} to Allow for this site.",
      "Return to Safari and refresh this page.",
    ],
    ref: { label: "Apple Support", url: "https://support.apple.com/guide/iphone/iphb01fc3c85/ios" },
  },
  android: {
    title:    "Allow {{perm}} access on Android",
    subtitle: "Follow these steps, then refresh the page.",
    steps: [
      "Tap the {{tune}} icon at the far left of the address bar.",
      "Tap Permissions and allow {{Perm}}.",
      "Refresh this page to apply the change.",
    ],
    ref: { label: "Google Support", url: "https://support.google.com/chrome/answer/2693767?co=GENIE.Platform%3DAndroid" },
  },
};

function detectBrowser(): BrowserKey {
  if (typeof navigator === "undefined") return "chrome";
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua) && /Chrome/i.test(ua)) return "android";
  if (/Edg\//i.test(ua)) return "edge";
  if (/Firefox/i.test(ua)) return "firefox";
  if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) return "safari";
  return "chrome";
}

function StepBadge({ number }: { number: number }) {
  return (
    <span style={{
      display:         "inline-flex",
      alignItems:      "center",
      justifyContent:  "center",
      width:           22,
      height:          22,
      borderRadius:    9999,
      backgroundColor: T.primary500,
      color:           T.white,
      fontSize:        12,
      fontWeight:      600,
      flexShrink:      0,
      marginTop:       1,
      fontFamily:      font,
    }}>
      {number}
    </span>
  );
}

// Each browser gets a visual mockup that matches what the user is supposed to
// look at on screen. Most browsers point at an icon in the URL bar; Safari
// points at the macOS menu bar instead.
type MockupVariant =
  | { kind: "urlbar"; iconToken: keyof typeof STEP_ICONS; pulseHighlight: boolean }
  | { kind: "menubar" };

const MOCKUP: Record<BrowserKey, MockupVariant> = {
  chrome:  { kind: "urlbar", iconToken: "tune", pulseHighlight: true },
  safari:  { kind: "menubar" },
  firefox: { kind: "urlbar", iconToken: "lock", pulseHighlight: true },
  edge:    { kind: "urlbar", iconToken: "tune", pulseHighlight: true },
  ios:     { kind: "urlbar", iconToken: "gear", pulseHighlight: true },
  android: { kind: "urlbar", iconToken: "tune", pulseHighlight: true },
};

const MOCKUP_CAPTION: Record<BrowserKey, string> = {
  chrome:  "Look for this icon at the far left of the address bar.",
  safari:  "Click Safari in the menu bar, then Settings…",
  firefox: "Click the lock icon at the far left of the address bar.",
  edge:    "Look for this icon at the far left of the address bar.",
  ios:     "Open the Settings app on your iPhone.",
  android: "Tap this icon at the far left of the address bar.",
};

function permissionGlyph(permTitle: string, color = T.primary500) {
  // permTitle is "Camera", "Microphone", or "Camera and Microphone". Mirror
  // each variant with the matching glyph so the header icon never lies about
  // which permission the user actually needs to enable.
  const lower = permTitle.toLowerCase();
  const hasCamera = lower.includes("camera");
  const hasMic = lower.includes("microphone");
  if (hasCamera && hasMic) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <Camera size={18} color={color} strokeWidth={2} />
        <Mic size={18} color={color} strokeWidth={2} />
      </span>
    );
  }
  if (hasMic) return <Mic size={22} color={color} strokeWidth={2} />;
  return <Camera size={22} color={color} strokeWidth={2} />;
}

function siteUrlPreview(): string {
  if (typeof window === "undefined") return "app.ourteam.ai/…";
  try {
    const host = window.location.host || "app.ourteam.ai";
    const path = window.location.pathname || "/";
    // Trim long paths so the URL bar mockup doesn't overflow visually.
    const shortPath =
      path.length > 14 ? `${path.slice(0, 14)}…` : path;
    return `${host}${shortPath}`;
  } catch {
    return "app.ourteam.ai/…";
  }
}

interface PermissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  permLower: string;
  permTitle: string;
  /**
   * Called when the user clicks the "Report this issue" link in the footer.
   * When provided, the modal closes and this fires (so a parent can open its
   * own IssuesDialog). When omitted, the link falls back to opening the
   * browser's support docs in a new tab.
   */
  onReportIssue?: () => void;
}

export function PermissionModal({ isOpen, onClose, permLower, permTitle, onReportIssue }: PermissionModalProps) {
  const [activeBrowser, setActiveBrowser] = useState<BrowserKey>(detectBrowser);

  useEffect(() => {
    if (isOpen) {
      setActiveBrowser(detectBrowser());
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  if (!isOpen) return null;

  const raw = INSTRUCTIONS[activeBrowser];
  const title = `How to enable ${permLower} access`;
  const subtitle = raw.subtitle;
  const steps = raw.steps.map((s) => resolveText(s, permLower, permTitle));
  const mockup = MOCKUP[activeBrowser];
  const mockupCaption = MOCKUP_CAPTION[activeBrowser];
  const urlPreview = siteUrlPreview();

  // Portal to document.body so the overlay's `position: fixed` resolves
  // against the viewport — otherwise an ancestor with `transform`/`filter`/
  // `will-change` becomes the containing block and leaves a gap.
  return createPortal(
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position:        "fixed",
        inset:           0,
        backgroundColor: T.overlay,
        display:         "flex",
        alignItems:      "center",
        justifyContent:  "center",
        zIndex:          T.zOverlay,
        padding:         16,
        fontFamily:      font,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        style={{
          backgroundColor: T.white,
          borderRadius:    16,
          boxShadow:       T.shadowModal,
          width:           480,
          maxWidth:        "100%",
          padding:         "20px 24px 20px 24px",
          zIndex:          T.zModal,
          animation:       "ot-pop-in 200ms ease-in-out",
          position:        "relative",
        }}
      >
        {/* Close button — absolute top-right */}
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position:     "absolute",
            top:          14,
            right:        14,
            background:   "none",
            border:       "none",
            cursor:       "pointer",
            padding:      4,
            color:        T.neutral400,
            display:      "flex",
            alignItems:   "center",
            borderRadius: 4,
            transition:   `color ${T.fast}`,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = T.neutral900)}
          onMouseLeave={(e) => (e.currentTarget.style.color = T.neutral400)}
        >
          <X size={18} strokeWidth={1.5} />
        </button>

        {/* Centered header */}
        <div style={{ textAlign: "center", marginTop: 8, marginBottom: 16 }}>
          <div
            style={{
              width:           48,
              height:          48,
              borderRadius:    9999,
              backgroundColor: T.primary50,
              display:         "inline-flex",
              alignItems:      "center",
              justifyContent:  "center",
              marginBottom:    14,
            }}
          >
            {permissionGlyph(permTitle)}
          </div>
          <h2
            id="modal-title"
            style={{
              fontSize:   18,
              fontWeight: 600,
              color:      T.neutral900,
              margin:     "0 0 6px 0",
              lineHeight: 1.3,
            }}
          >
            {title}
          </h2>
          <p style={{ fontSize: 13, color: T.neutral600, margin: 0 }}>{subtitle}</p>
        </div>

        {/* Mockup — URL bar for most browsers; menu bar for Safari since its
            permission flow starts in the menu bar, not the address bar. */}
        <div
          style={{
            backgroundColor: T.neutral100,
            border:          `1px solid ${T.neutral200}`,
            borderRadius:    10,
            padding:         "12px 14px 10px 14px",
            marginBottom:    16,
          }}
        >
          {mockup.kind === "urlbar" ? (
            <div
              style={{
                backgroundColor: T.white,
                border:          `1px solid ${T.neutral200}`,
                borderRadius:    8,
                padding:         "8px 10px",
                display:         "flex",
                alignItems:      "center",
                gap:             10,
              }}
            >
              {mockup.pulseHighlight ? (
                <span
                  style={{
                    position:   "relative",
                    width:      32,
                    height:     32,
                    flexShrink: 0,
                  }}
                >
                  {/* Two staggered pulse rings — pure CSS, GPU-friendly */}
                  <span
                    className="ot-pulse-ring"
                    style={{
                      position:     "absolute",
                      inset:        0,
                      borderRadius: 9999,
                      border:       `2px solid ${T.primary400}`,
                    }}
                  />
                  <span
                    className="ot-pulse-ring ot-pulse-ring--delay"
                    style={{
                      position:     "absolute",
                      inset:        0,
                      borderRadius: 9999,
                      border:       `2px solid ${T.primary400}`,
                    }}
                  />
                  <span
                    style={{
                      position:        "absolute",
                      inset:           0,
                      display:         "flex",
                      alignItems:      "center",
                      justifyContent:  "center",
                      borderRadius:    9999,
                      backgroundColor: T.primary50,
                    }}
                  >
                    {STEP_ICONS[mockup.iconToken]}
                  </span>
                </span>
              ) : (
                <span
                  style={{
                    display:         "inline-flex",
                    alignItems:      "center",
                    justifyContent:  "center",
                    flexShrink:      0,
                  }}
                >
                  {STEP_ICONS[mockup.iconToken]}
                </span>
              )}
              <span
                style={{
                  flex:        1,
                  fontSize:    13,
                  color:       T.neutral700,
                  fontFamily:  "ui-monospace, SFMono-Regular, Menlo, monospace",
                  overflow:    "hidden",
                  whiteSpace:  "nowrap",
                  textOverflow:"ellipsis",
                  minWidth:    0,
                }}
              >
                {urlPreview}
              </span>
              <MoreHorizontal size={16} color={T.neutral400} strokeWidth={2} />
            </div>
          ) : (
            /* Safari macOS menu bar mockup */
            <div
              style={{
                backgroundColor: "#1C1C1E",
                borderRadius:    8,
                padding:         "6px 14px",
                display:         "flex",
                alignItems:      "center",
                gap:             14,
                color:           T.white,
                fontSize:        13,
              }}
            >
              <Apple size={14} color={T.white} fill={T.white} strokeWidth={0} />
              <span
                style={{
                  backgroundColor: T.primary500,
                  color:           T.white,
                  padding:         "2px 10px",
                  borderRadius:    5,
                  fontWeight:      500,
                }}
              >
                Safari
              </span>
              <span>File</span>
              <span>Edit</span>
              <span>View</span>
            </div>
          )}
          <p
            style={{
              fontSize:  12,
              color:     T.neutral400,
              margin:    "8px 0 0 0",
              textAlign: "center",
            }}
          >
            {mockupCaption}
          </p>
        </div>

        {/* Steps */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
          {steps.map((step, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
              <StepBadge number={i + 1} />
              <span style={{ fontSize: 13, color: T.neutral700, lineHeight: 1.6 }}>
                {renderStep(step)}
              </span>
            </div>
          ))}
        </div>

        {/* Footer buttons */}
        <div
          style={{
            display:        "flex",
            justifyContent: "flex-end",
            gap:            10,
            paddingTop:     16,
            borderTop:      `1px solid ${T.neutral200}`,
            marginBottom:   14,
          }}
        >
          <button
            onClick={onClose}
            style={{
              minWidth:        96,
              height:          40,
              padding:         "0 18px",
              backgroundColor: T.white,
              color:           T.neutral700,
              border:          `1px solid ${T.neutral200}`,
              borderRadius:    8,
              fontSize:        14,
              fontWeight:      500,
              cursor:          "pointer",
              fontFamily:      font,
              transition:      `background-color ${T.fast}`,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = T.neutral100)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = T.white)}
          >
            Close
          </button>
          <button
            onClick={onClose}
            style={{
              minWidth:        120,
              height:          40,
              padding:         "0 18px",
              backgroundColor: T.primary500,
              color:           T.white,
              border:          "none",
              borderRadius:    8,
              fontSize:        14,
              fontWeight:      600,
              cursor:          "pointer",
              fontFamily:      font,
              transition:      `background-color ${T.fast}`,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = T.primary400)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = T.primary500)}
          >
            I understand
          </button>
        </div>

        {/* Help footer */}
        <p
          style={{
            fontSize:  12,
            color:     T.neutral400,
            margin:    0,
            textAlign: "center",
          }}
        >
          Still stuck?{" "}
          {onReportIssue ? (
            <button
              type="button"
              onClick={() => {
                onClose();
                onReportIssue();
              }}
              style={{
                background:     "none",
                border:         "none",
                padding:        0,
                font:           "inherit",
                color:          T.primary500,
                textDecoration: "underline",
                cursor:         "pointer",
                fontFamily:     font,
              }}
            >
              Report this issue
            </button>
          ) : (
            <a
              href={raw.ref.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color:          T.primary500,
                textDecoration: "underline",
                fontFamily:     font,
              }}
            >
              Report this issue
            </a>
          )}{" "}
          and our team will help.
        </p>
      </div>

      <style>{`
        @keyframes ot-pop-in {
          from { opacity: 0; transform: scale(0.96) translateY(6px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);   }
        }
        @keyframes ot-pulse-ring {
          0%   { transform: scale(0.85); opacity: 0.7; }
          70%  { opacity: 0; }
          100% { transform: scale(2);   opacity: 0;   }
        }
        .ot-pulse-ring {
          animation: ot-pulse-ring 1.8s ease-out infinite;
          transform-origin: center;
          pointer-events: none;
        }
        .ot-pulse-ring--delay {
          animation-delay: 0.9s;
        }
      `}</style>
    </div>,
    document.body
  );
}

interface MediaPermissionWarningProps {
  cameraGranted?: boolean;
  micGranted?: boolean;
  /** Forwarded to PermissionModal so the parent can wire up an IssuesDialog. */
  onReportIssue?: () => void;
}

export default function MediaPermissionWarning({ cameraGranted = false, micGranted = false, onReportIssue }: MediaPermissionWarningProps) {
  const [modalOpen, setModalOpen] = useState(false);

  const openModal  = useCallback(() => setModalOpen(true),  []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  if (cameraGranted && micGranted) return null;

  const permLower = !cameraGranted && !micGranted
    ? "camera and microphone"
    : !cameraGranted
      ? "camera"
      : "microphone";

  const permTitle = !cameraGranted && !micGranted
    ? "Camera and Microphone"
    : !cameraGranted
      ? "Camera"
      : "Microphone";

  const inlineLabel = !cameraGranted && !micGranted
    ? "camera & microphone"
    : !cameraGranted
      ? "camera"
      : "microphone";

  return (
    <>
      <div style={{
        display:    "flex",
        alignItems: "center",
        gap:        6,
        fontFamily: font,
        flexWrap:   "wrap",
      }}>
        <AlertTriangle size={14} color={T.warning500} strokeWidth={1.5} style={{ flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 500, color: T.warning700 }}>
          Enable your {inlineLabel} to start.
        </span>
        <button
          onClick={openModal}
          style={{
            background:     "none",
            border:         "none",
            padding:        0,
            fontSize:       13,
            color:          T.primary500,
            cursor:         "pointer",
            textDecoration: "underline",
            fontFamily:     font,
            fontWeight:     400,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = T.primary400)}
          onMouseLeave={(e) => (e.currentTarget.style.color = T.primary500)}
        >
          Here's how.
        </button>
      </div>

      <PermissionModal
        isOpen={modalOpen}
        onClose={closeModal}
        permLower={permLower}
        permTitle={permTitle}
        onReportIssue={onReportIssue}
      />
    </>
  );
}
